import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/lib/config/env';
import { STORAGE_KEYS } from '@/lib/config/constants';
import { authStorage } from '@/lib/utils/authStorage';
import { useAuthStore } from '@/store/authStore';
import type { ApiError } from '@/types/common';

class HttpClient {
  private instance: AxiosInstance;
  private refreshPromise: Promise<string | null> | null = null;
  private onUnauthorized?: () => void;

  constructor() {
    this.instance = axios.create({
      baseURL: env.apiBaseUrl,
      timeout: env.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.instance.interceptors.request.use(this.handleRequest);
    this.instance.interceptors.response.use(
      (res) => res,
      (err) => this.handleError(err),
    );
  }

  setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  refreshToken(): Promise<string | null> {
    return this.refreshAccessToken();
  }

  private handleRequest = async (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    let token = useAuthStore.getState().token;
    const isAuthEndpoint =
      config.url?.includes('/auth/login') || config.url?.includes('/auth/refresh');

    const online = typeof navigator === 'undefined' || navigator.onLine;
    if (token && !isAuthEndpoint && online && this.isTokenExpired(token)) {
      token = (await this.refreshAccessToken()) ?? token;
    }

    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  };

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]!)) as { exp?: number };
      if (typeof payload.exp !== 'number') return false;
      return payload.exp * 1000 <= Date.now() + 5000;
    } catch {
      return false;
    }
  }

  private handleError = async (error: AxiosError): Promise<never> => {
    const status = error.response?.status;
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const isAuthEndpoint =
      original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`);
        return this.instance.request(original) as unknown as never;
      }
      this.onUnauthorized?.();
      return new Promise(() => {}) as never;
    }

    const apiError: ApiError = {
      message:
        (error.response?.data as { message?: string } | undefined)?.message ??
        error.message ??
        'Request failed',
      code: (error.response?.data as { code?: string } | undefined)?.code,
      status,
      details: (error.response?.data as { details?: Record<string, string[]> } | undefined)
        ?.details,
      data: error.response?.data,
    };
    return Promise.reject(apiError);
  };

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    const refreshToken = authStorage.get<string | null>(STORAGE_KEYS.refreshToken, null);
    if (!refreshToken) return null;

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post<{ token: string; refreshToken?: string }>(
          `${env.apiBaseUrl}/auth/refresh`,
          { refreshToken },
        );
        authStorage.set(STORAGE_KEYS.authToken, response.data.token);
        if (response.data.refreshToken) {
          authStorage.set(STORAGE_KEYS.refreshToken, response.data.refreshToken);
        }
        useAuthStore.getState().setTokens(response.data.token, response.data.refreshToken);
        return response.data.token;
      } catch (err) {
        const serverRejected = axios.isAxiosError(err) && !!err.response;
        if (serverRejected) {
          authStorage.remove(STORAGE_KEYS.authToken);
          authStorage.remove(STORAGE_KEYS.refreshToken);
          authStorage.remove(STORAGE_KEYS.authUser);
          useAuthStore.getState().clearSession();
        }
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.get<T>(url, config).then((r) => r.data);
  }

  post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.post<T>(url, data, config).then((r) => r.data);
  }

  put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.put<T>(url, data, config).then((r) => r.data);
  }

  patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.patch<T>(url, data, config).then((r) => r.data);
  }

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.delete<T>(url, config).then((r) => r.data);
  }

  upload<T>(url: string, file: File, fieldName = 'image'): Promise<T> {
    const form = new FormData();
    form.append(fieldName, file);
    return this.instance
      .post<T>(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  }

  getRaw(): AxiosInstance {
    return this.instance;
  }
}

export const httpClient = new HttpClient();
